import { Component } from '@angular/core';
import { Location } from '@angular/common';
import { Validators, FormGroup, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin } from 'rxjs';
import { CartaPorteService } from 'src/app/services/carta-porte.service';
import { OrderService } from 'src/app/services/order.service';
import { TrailerService } from 'src/app/services/trailer.service';
import { UtilsService } from 'src/app/services/utils.service';

declare const $: any;

@Component({
  selector: 'app-carta-porte-edit',
  templateUrl: './carta-porte-edit.component.html',
  styleUrls: ['./carta-porte-edit.component.css']
})
export class CartaPorteEditComponent {
  private idCartaPorte: number = 0;
  private readonly destinationAddressFields = [
    'destination_calle',
    'destination_codigo_postal',
    'destination_pais',
    'destination_estado',
    'destination_municipio',
    'destination_localidad',
    'destination_colonia'
  ];
  private readonly originAddressFields = [
    'origin_calle',
    'origin_codigo_postal',
    'origin_pais',
    'origin_estado',
    'origin_municipio',
    'origin_localidad',
    'origin_colonia'
  ];
  private readonly addressFieldsToRemove = [
    ...this.originAddressFields,
    ...this.destinationAddressFields
  ];
  private readonly cfdiTaxesDefaults = {
    TotalImpuestosTrasladados: '1600.00',
    Traslados: [
      {
        Base: '10000.00',
        Impuesto: '002',
        TipoFactor: 'Tasa',
        TasaOCuota: '0.160000',
        Importe: '1600.00',
      }
    ]
  };
  private readonly defaultSubtotal = '10000.00';
  taxRatePercentDisplay = '16.00';
  private cfdiCalcInitialized = false;

  colonias: any[] = [];
  loadingColonias: boolean = false;
  dataDestination: any = {};
  existCode: any = 0;
  coloniasOrigen: any[] = [];
  loadingColoniasOrigen: boolean = false;
  dataOrigin: any = {};
  existCodeOrigen: any = 0;

  constructor(
    private fb: FormBuilder,
    private utils: UtilsService,
    private spinner: NgxSpinnerService,
    private apiCartaPorte: CartaPorteService,
    private apiOrder: OrderService,
    private apiTrailer: TrailerService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {
    this.spinner.show();
    this.route.params.subscribe(params => {
      this.idCartaPorte = Number(params['id']);
      this.loadAll();
    });
  }

  private loadTrailerForSelectedService() {
    const requiresTrailer = !!(this.selectedService?.requires_trailer === true || this.selectedService?.requires_trailer === 1);
    const trailerId = Number(this.selectedService?.trailer_id);

    if (!requiresTrailer || !trailerId) {
      this.selectedTrailer = null;
      return;
    }

    this.apiTrailer.getId(trailerId).subscribe((resp: any) => {
      this.selectedTrailer = resp || null;
    }, () => {
      this.selectedTrailer = null;
    });
  }

  selectedService: any = null;
  selectedTrailer: any = null;

  services: any[] = [];
  transportMeans: any[] = [];
  weightUnits: any[] = [];
  logisticsTypes: any[] = [];
  measurementUnits: any[] = [];
  hazardousMaterialOptions: any[] = [];
  unitKeys: any[] = [];
  cargoInsurers: any[] = [];

  cartaPorteForm!: FormGroup;

  private loadDistanceForService(serviceId: number) {
    if (!serviceId) {
      return;
    }

    this.apiOrder.getDistanceRoute(serviceId).subscribe((resp: any) => {
      const km = resp?.total_km;
      if (km !== null && km !== undefined && km !== '') {
        this.cartaPorteForm.patchValue({ total_distance: Number(km) });
      }
    }, () => { });
  }

  private mapServiceForSelect(service: any) {
    if (!service) {
      return null;
    }

    return {
      id: service?.id,
      client: service?.client?.name ?? service?.client ?? '',
      unit: service?.unit?.unit ?? service?.unit ?? '',
      destination_address: service?.destination_address ?? null,
      destination_components: service?.destination_components ? JSON.parse(service?.destination_components) : null
    };
  }

  ngOnInit(): void {
    this.cartaPorteForm = this.fb.group({
      service_id: ['', Validators.required],
      transport_mean_id: ['', Validators.required],
      total_distance: ['', Validators.required],
      gross_weight_total: ['', Validators.required],
      weight_unit_id: ['', Validators.required],
      total_goods: ['', Validators.required],
      logistics_type_id: ['', Validators.required],
      dimensions: ['', Validators.required],
      measurement_unit_id: ['', Validators.required],
      hazardous_material_option_id: ['', Validators.required],
      unit_key_id: ['', Validators.required],
      cargo_insurer_id: [''],
      cargo_insurance_policy_number: [''],
      insurance_premium_value: [''],
      origin_calle: [{ value: '', disabled: true }],
      origin_codigo_postal: ['', Validators.required],
      origin_pais: [{ value: '', disabled: true }],
      origin_estado: [{ value: '', disabled: true }],
      origin_municipio: [{ value: '', disabled: true }],
      origin_localidad: [{ value: '', disabled: true }],
      origin_colonia: ['', Validators.required],
      origin_address: [null],
      destination_calle: [{ value: '', disabled: true }],
      destination_codigo_postal: ['', Validators.required],
      destination_pais: [{ value: '', disabled: true }],
      destination_estado: [{ value: '', disabled: true }],
      destination_municipio: [{ value: '', disabled: true }],
      destination_localidad: [{ value: '', disabled: true }],
      destination_colonia: ['', Validators.required],
      destination_address: [null],
      cfdi_subtotal: [this.defaultSubtotal, Validators.required],
      cfdi_total: [
        (Number(this.defaultSubtotal) + Number(this.cfdiTaxesDefaults.TotalImpuestosTrasladados)).toFixed(2),
        Validators.required
      ],
      cfdi_total_impuestos_trasladados: [this.cfdiTaxesDefaults.TotalImpuestosTrasladados, Validators.required],
      cfdi_traslado_base: [this.cfdiTaxesDefaults.Traslados[0].Base, Validators.required],
      cfdi_traslado_impuesto: [this.cfdiTaxesDefaults.Traslados[0].Impuesto, Validators.required],
      cfdi_traslado_tipo_factor: [this.cfdiTaxesDefaults.Traslados[0].TipoFactor, Validators.required],
      cfdi_traslado_tasa_cuota: [this.cfdiTaxesDefaults.Traslados[0].TasaOCuota, Validators.required],
      cfdi_traslado_importe: [this.cfdiTaxesDefaults.Traslados[0].Importe, Validators.required],
    });

    this.setupCfdiRecalculation();
    this.setupDestinationAddress();
    this.setupOriginAddress();
    this.setupPostalCodeListener();
  }

  private loadAll() {
    if (!this.cartaPorteForm) {
      // ensure form is ready even if constructor triggers first
      this.ngOnInit();
    }

    forkJoin({
      cartaPorte: this.apiCartaPorte.getId(this.idCartaPorte),
      transportMeans: this.apiCartaPorte.getTransportMeans(),
      weightUnits: this.apiCartaPorte.getWeightUnits(),
      logisticsTypes: this.apiCartaPorte.getLogisticsTypes(),
      measurementUnits: this.apiCartaPorte.getMeasurementUnits(),
      hazardousMaterialOptions: this.apiCartaPorte.getHazardousMaterialOptions(),
      unitKeys: this.apiCartaPorte.getUnitKeys(),
      cargoInsurers: this.apiCartaPorte.getCargoInsurers(),
    }).subscribe((resp: any) => {
      this.transportMeans = resp.transportMeans || [];
      this.weightUnits = resp.weightUnits || [];
      this.logisticsTypes = resp.logisticsTypes || [];
      this.measurementUnits = resp.measurementUnits || [];
      this.hazardousMaterialOptions = resp.hazardousMaterialOptions || [];
      this.unitKeys = resp.unitKeys || [];
      this.cargoInsurers = resp.cargoInsurers || [];

      const cp = resp?.cartaPorte || {};

      const serviceId = Number(cp?.service?.id || cp?.service_id || 0);
      if (serviceId) {
        this.apiOrder.getId(serviceId).subscribe((serviceResp: any) => {
          this.selectedService = serviceResp || null;
          this.loadTrailerForSelectedService();
          
          // Set destination_calle with service's destination_address
          if (serviceResp?.destination_address) {
            this.cartaPorteForm.patchValue({
              destination_calle: serviceResp.destination_address
            }, { emitEvent: false });
          }
          
          // Set origin_calle with service's origin_address if available
          if (serviceResp?.origin_address) {
            this.cartaPorteForm.patchValue({
              origin_calle: serviceResp.origin_address
            }, { emitEvent: false });
          }
        }, () => { });
      }

      const taxesFormValues = this.mapCfdiTaxesToForm(cp?.cfdi_taxes);
      const subtotalValue = this.formatNumberString(cp?.cfdi_subtotal, 2)
        ?? taxesFormValues.cfdi_traslado_base
        ?? this.defaultSubtotal;

      const totalTaxesValue = taxesFormValues.cfdi_total_impuestos_trasladados
        ?? this.cfdiTaxesDefaults.TotalImpuestosTrasladados;

      const totalValue = this.formatNumberString(cp?.cfdi_total, 2)
        ?? (Number(subtotalValue) + Number(totalTaxesValue)).toFixed(2);

      // Handle address data from existing carta porte
      const addressData = cp?.destination_address;
      if (addressData) {
        if (addressData.c_CodigoPostal) {
          // Sepomex format
          this.dataDestination = addressData;
          this.fetchColoniasByPostalCode(addressData.c_CodigoPostal, addressData.c_Colonia || addressData.coloniaDescripcion);
          this.cartaPorteForm.patchValue({
            destination_codigo_postal: addressData.c_CodigoPostal || '',
            destination_pais: 'MEX',
            destination_estado: addressData.estadoDescripcion || '',
            destination_municipio: addressData.municipioDescripcion || '',
            destination_localidad: addressData.localidadDescripcion || '',
          }, { emitEvent: false });
        } else {
          // Basic format
          this.cartaPorteForm.patchValue({
            destination_codigo_postal: addressData.codigo_postal || '',
            destination_pais: addressData.pais || 'MEX',
            destination_estado: addressData.estado || '',
            destination_municipio: addressData.municipio || '',
            destination_localidad: addressData.localidad || '',
            destination_colonia: addressData.colonia || '',
          }, { emitEvent: false });
        }
      }

      // Handle origin address data from existing carta porte
      const originAddressData = cp?.origin_address;
      if (originAddressData) {
        if (originAddressData.c_CodigoPostal) {
          // Sepomex format
          this.dataOrigin = originAddressData;
          this.fetchColoniasByPostalCodeOrigen(originAddressData.c_CodigoPostal, originAddressData.c_Colonia || originAddressData.coloniaDescripcion);
          this.cartaPorteForm.patchValue({
            origin_codigo_postal: originAddressData.c_CodigoPostal || '',
            origin_pais: 'MEX',
            origin_estado: originAddressData.estadoDescripcion || '',
            origin_municipio: originAddressData.municipioDescripcion || '',
            origin_localidad: originAddressData.localidadDescripcion || '',
          }, { emitEvent: false });
        } else {
          // Basic format
          this.cartaPorteForm.patchValue({
            origin_codigo_postal: originAddressData.codigo_postal || '',
            origin_pais: originAddressData.pais || 'MEX',
            origin_estado: originAddressData.estado || '',
            origin_municipio: originAddressData.municipio || '',
            origin_localidad: originAddressData.localidad || '',
            origin_colonia: originAddressData.colonia || '',
          }, { emitEvent: false });
        }
      }

      
      this.cartaPorteForm.patchValue({
        service_id: cp?.service?.id || cp?.service_id || '',
        transport_mean_id: cp?.transport_mean?.id || cp?.transport_mean_id || '',
        total_distance: cp?.total_distance ?? '',
        gross_weight_total: cp?.gross_weight_total ?? '',
        weight_unit_id: cp?.weight_unit?.id || cp?.weight_unit_id || '',
        total_goods: cp?.total_goods ?? '',
        logistics_type_id: cp?.logistics_type?.id || cp?.logistics_type_id || '',
        dimensions: cp?.dimensions ?? '',
        measurement_unit_id: cp?.measurement_unit?.id || cp?.measurement_unit_id || '',
        hazardous_material_option_id: cp?.hazardous_material_option?.id || cp?.hazardous_material_option_id || '',
        unit_key_id: cp?.unit_key?.id || cp?.unit_key_id || '',
        cargo_insurer_id: cp?.cargo_insurer?.id || cp?.cargo_insurer_id || '',
        cargo_insurance_policy_number: cp?.cargo_insurance_policy_number ?? '',
        insurance_premium_value: cp?.insurance_premium_value ?? '',
        cfdi_subtotal: subtotalValue,
        cfdi_total: totalValue,
        ...taxesFormValues
      }, { emitEvent: false });

      this.recalculateCfdiTaxes();
      this.updateDestinationAddress();
      this.updateOriginAddress();

      this.spinner.hide();
    }, () => {
      this.spinner.hide();
    });
  }

  saveAndStampCfdi() {
    const serviceId = Number(this.cartaPorteForm.getRawValue()?.service_id);
    if (!serviceId) {
      this.utils.alertSwal({ title: 'Advertencia', text: 'Seleccione un servicio', icon: 'warning' });
      return;
    }

    if (!this.cartaPorteForm.valid) {
      this.utils.alertSwal({ title: 'Advertencia', text: 'Verifique que la información se encuentre completa.', icon: 'warning' });
      return;
    }

    this.spinner.show();
    
    // First save the carta porte information
    this.updateCartaPorteInternal().then((saveResult: boolean) => {
      if (!saveResult) {
        this.spinner.hide();
        return;
      }
      
      // Then proceed with stamping
      this.apiCartaPorte.stampCfdi(serviceId).subscribe((resp: any) => {
        this.spinner.hide();
        this.utils.alertSwal({ title: 'Exitoso', text: resp?.msg || 'Timbrado realizado', icon: 'success' })
          .then(() => {
            this.router.navigateByUrl('/carta-porte/list');
          });
      }, (err: any) => {
        this.spinner.hide();
        this.utils.alertSwal({
          title: 'Advertencia',
          text: this.extractErrorMessage(err, 'Timbrado no disponible'),
          icon: 'warning'
        });
      });
    });
  }

  private updateCartaPorteInternal(): Promise<boolean> {
    return new Promise((resolve) => {
      const raw = this.cartaPorteForm.getRawValue();
      const payload: any = { ...raw };
      payload.service_id = Number(payload.service_id);
      payload.transport_mean_id = Number(payload.transport_mean_id);
      payload.weight_unit_id = Number(payload.weight_unit_id);
      payload.logistics_type_id = Number(payload.logistics_type_id);
      payload.measurement_unit_id = Number(payload.measurement_unit_id);
      payload.hazardous_material_option_id = Number(payload.hazardous_material_option_id);
      payload.unit_key_id = Number(payload.unit_key_id);
      payload.cargo_insurer_id = payload.cargo_insurer_id !== '' && payload.cargo_insurer_id !== null
        ? Number(payload.cargo_insurer_id)
        : payload.cargo_insurer_id;
      payload.cfdi_taxes = this.buildCfdiTaxesPayload(raw);

      const formattedSubtotal = this.formatNumberString(raw?.cfdi_subtotal, 2) ?? this.defaultSubtotal;
      const formattedTaxesTotal = this.formatNumberString(raw?.cfdi_total_impuestos_trasladados, 2)
        ?? this.cfdiTaxesDefaults.TotalImpuestosTrasladados;
      const formattedTotal = this.formatNumberString(raw?.cfdi_total, 2)
        ?? (Number(formattedSubtotal) + Number(formattedTaxesTotal)).toFixed(2);

      payload.cfdi_subtotal = formattedSubtotal;
      payload.cfdi_total = formattedTotal;

      // Eliminar campos individuales de dirección del payload final, solo dejar origin_address y destination_address
      this.addressFieldsToRemove.forEach(field => {
        delete payload[field];
      });

      this.apiCartaPorte.update(payload, this.idCartaPorte).subscribe((resp: any) => {
        resolve(true);
      }, (err: any) => {
        this.spinner.hide();
        const msg = err?.error ? JSON.stringify(err.error) : 'Se ha presentado un error al actualizar carta porte';
        this.utils.alertSwal({ title: 'Error!', text: msg, icon: 'error' });
        resolve(false);
      });
    });
  }

  updateCartaPorte() {
    if (this.cartaPorteForm.valid) {
      this.spinner.show();
      const raw = this.cartaPorteForm.getRawValue();
      const payload: any = { ...raw };
      payload.service_id = Number(payload.service_id);
      payload.transport_mean_id = Number(payload.transport_mean_id);
      payload.weight_unit_id = Number(payload.weight_unit_id);
      payload.logistics_type_id = Number(payload.logistics_type_id);
      payload.measurement_unit_id = Number(payload.measurement_unit_id);
      payload.hazardous_material_option_id = Number(payload.hazardous_material_option_id);
      payload.unit_key_id = Number(payload.unit_key_id);
      payload.cargo_insurer_id = payload.cargo_insurer_id !== '' && payload.cargo_insurer_id !== null
        ? Number(payload.cargo_insurer_id)
        : payload.cargo_insurer_id;
      payload.cfdi_taxes = this.buildCfdiTaxesPayload(raw);

      const formattedSubtotal = this.formatNumberString(raw?.cfdi_subtotal, 2) ?? this.defaultSubtotal;
      const formattedTaxesTotal = this.formatNumberString(raw?.cfdi_total_impuestos_trasladados, 2)
        ?? this.cfdiTaxesDefaults.TotalImpuestosTrasladados;
      const formattedTotal = this.formatNumberString(raw?.cfdi_total, 2)
        ?? (Number(formattedSubtotal) + Number(formattedTaxesTotal)).toFixed(2);

      payload.cfdi_subtotal = formattedSubtotal;
      payload.cfdi_total = formattedTotal;

      // Eliminar campos individuales de dirección del payload final, solo dejar origin_address y destination_address
      this.addressFieldsToRemove.forEach(field => {
        delete payload[field];
      });

      
      this.apiCartaPorte.update(payload, this.idCartaPorte).subscribe((resp: any) => {
        this.spinner.hide();
        this.utils.alertSwal({ title: 'Exitoso', text: resp.msg, icon: 'success' })
          .then(() => {
            this.router.navigateByUrl('/carta-porte/list');
          });
      }, (err: any) => {
        this.spinner.hide();
        const msg = err?.error ? JSON.stringify(err.error) : 'Se ha presentado un error al actualizar carta porte';
        this.utils.alertSwal({ title: 'Error!', text: msg, icon: 'error' });
      });
    } else {
      this.utils.alertSwal({ title: 'Advertencia', text: 'Verifique que la información se encuentre completa.', icon: 'warning' });
    }
  }

  get registerFormControl() {
    return this.cartaPorteForm.controls;
  }

  getSelectedColoniaCode(): string {
    const selectedColoniaValue = this.cartaPorteForm.get('destination_colonia')?.value;
    if (!selectedColoniaValue) {
      return 'MEX'; // Default value
    }
    
    // Find the colonia object by c_Colonia
    const selectedColonia = this.colonias.find(c => c.c_Colonia === selectedColoniaValue);
    return selectedColonia ? selectedColonia.c_Colonia : selectedColoniaValue;
  }

  // Método para manejar cambios en el input de subtotal
  onSubtotalChange(event: any): void {
    // Solo forzar recálculo de impuestos, permitir escritura libre
    this.recalculateCfdiTaxes();
  }

  private mapCfdiTaxesToForm(cfdiTaxes: any) {
    const taxes = cfdiTaxes && Array.isArray(cfdiTaxes?.Traslados) && cfdiTaxes.Traslados.length > 0
      ? cfdiTaxes
      : this.cfdiTaxesDefaults;
    const first = taxes.Traslados[0];
    return {
      cfdi_total_impuestos_trasladados: taxes?.TotalImpuestosTrasladados ?? this.cfdiTaxesDefaults.TotalImpuestosTrasladados,
      cfdi_traslado_base: first?.Base ?? this.cfdiTaxesDefaults.Traslados[0].Base,
      cfdi_traslado_impuesto: first?.Impuesto ?? this.cfdiTaxesDefaults.Traslados[0].Impuesto,
      cfdi_traslado_tipo_factor: first?.TipoFactor ?? this.cfdiTaxesDefaults.Traslados[0].TipoFactor,
      cfdi_traslado_tasa_cuota: first?.TasaOCuota ?? this.cfdiTaxesDefaults.Traslados[0].TasaOCuota,
      cfdi_traslado_importe: first?.Importe ?? this.cfdiTaxesDefaults.Traslados[0].Importe,
    };
  }

  private buildCfdiTaxesPayload(raw: any) {
    return {
      TotalImpuestosTrasladados: this.formatNumberString(raw?.cfdi_total_impuestos_trasladados, 2) ?? this.cfdiTaxesDefaults.TotalImpuestosTrasladados,
      Traslados: [
        {
          Base: this.formatNumberString(raw?.cfdi_traslado_base, 2) ?? this.cfdiTaxesDefaults.Traslados[0].Base,
          Impuesto: raw?.cfdi_traslado_impuesto ?? this.cfdiTaxesDefaults.Traslados[0].Impuesto,
          TipoFactor: raw?.cfdi_traslado_tipo_factor ?? this.cfdiTaxesDefaults.Traslados[0].TipoFactor,
          TasaOCuota: this.formatNumberString(raw?.cfdi_traslado_tasa_cuota, 6) ?? this.cfdiTaxesDefaults.Traslados[0].TasaOCuota,
          Importe: this.formatNumberString(raw?.cfdi_traslado_importe, 2) ?? this.cfdiTaxesDefaults.Traslados[0].Importe,
        }
      ]
    };
  }

  private formatNumberString(value: any, decimals: number): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const num = Number(value);
    if (!isNaN(num)) {
      return num.toFixed(decimals);
    }

    return String(value);
  }

  private extractErrorMessage(err: any, fallback: string): string {
    return err?.error?.error?.conectia_response?.message
      || err?.error?.conectia_response?.message
      || err?.error?.error?.message
      || err?.error?.error
      || err?.error?.message
      || (err?.error ? JSON.stringify(err.error) : null)
      || err?.message
      || fallback;
  }

  private setupCfdiRecalculation() {
    if (this.cfdiCalcInitialized) {
      return;
    }
    this.cfdiCalcInitialized = true;

    ['cfdi_subtotal', 'cfdi_traslado_tasa_cuota'].forEach(controlName => {
      this.cartaPorteForm.get(controlName)?.valueChanges.subscribe(() => {
        this.recalculateCfdiTaxes();
      });
    });

    this.recalculateCfdiTaxes();
  }

  private recalculateCfdiTaxes() {
    const subtotalControl = this.cartaPorteForm.get('cfdi_subtotal');
    let subtotal = Number(subtotalControl?.value);
    if (isNaN(subtotal)) {
      subtotal = Number(this.defaultSubtotal);
    }
    subtotal = this.round(subtotal, 2);

    const rateControl = this.cartaPorteForm.get('cfdi_traslado_tasa_cuota');
    let rate = Number(rateControl?.value);
    if (isNaN(rate)) {
      rate = Number(this.cfdiTaxesDefaults.Traslados[0].TasaOCuota);
    }
    rate = this.round(rate, 6);

    const importe = this.round(subtotal * rate, 2);
    const total = this.round(subtotal + importe, 2);

    this.taxRatePercentDisplay = (rate * 100).toFixed(2);

    this.patchIfChanged('cfdi_subtotal', subtotal.toFixed(2));
    this.patchIfChanged('cfdi_traslado_base', subtotal.toFixed(2));
    this.patchIfChanged('cfdi_traslado_importe', importe.toFixed(2));
    this.patchIfChanged('cfdi_total_impuestos_trasladados', importe.toFixed(2));
    this.patchIfChanged('cfdi_total', total.toFixed(2));
  }

  private patchIfChanged(controlName: string, nextValue: string) {
    const control = this.cartaPorteForm.get(controlName);
    if (control && control.value !== nextValue) {
      control.patchValue(nextValue, { emitEvent: false });
    }
  }

  private round(value: number, precision: number): number {
    const multiplier = Math.pow(10, precision);
    return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
  }

  private setupDestinationAddress() {
    // Escuchar cambios en los campos individuales de dirección
    const addressFields = this.destinationAddressFields;

    this.setupAddressListeners(addressFields, () => this.updateDestinationAddress());
  }

  private setupOriginAddress() {
    // Escuchar cambios en los campos individuales de dirección origen
    const addressFields = this.originAddressFields;

    this.setupAddressListeners(addressFields, () => this.updateOriginAddress());
  }

  private updateOriginAddress() {
    // If we have Sepomex data, use that structure
    if (this.dataOrigin && this.dataOrigin.c_CodigoPostal) {
      const selectedColonia = this.coloniasOrigen.find(c => c.c_Colonia === this.cartaPorteForm.get('origin_colonia')?.value);
      
      const sepomexData = {
        c_CodigoPostal: this.dataOrigin.c_CodigoPostal,
        c_Estado: this.dataOrigin.c_Estado,
        estadoDescripcion: this.dataOrigin.estadoDescripcion,
        c_Municipio: this.dataOrigin.c_Municipio,
        municipioDescripcion: this.dataOrigin.municipioDescripcion,
        c_Localidad: this.dataOrigin.c_Localidad,
        localidadDescripcion: this.dataOrigin.localidadDescripcion,
        c_Colonia: selectedColonia?.c_Colonia || '',
        coloniaDescripcion: selectedColonia?.descripcion || ''
      };
      
      this.cartaPorteForm.get('origin_address')?.setValue(sepomexData, { emitEvent: false });
    } else {
      // Fallback to basic structure if no Sepomex data (use SAT format for consistency)
      const addressData = {
        c_CodigoPostal: this.cartaPorteForm.get('origin_codigo_postal')?.value || '',
        c_Estado: this.cartaPorteForm.get('origin_estado')?.value || '',
        estadoDescripcion: this.cartaPorteForm.get('origin_estado')?.value || '',
        c_Municipio: this.cartaPorteForm.get('origin_municipio')?.value || '',
        municipioDescripcion: this.cartaPorteForm.get('origin_municipio')?.value || '',
        c_Localidad: this.cartaPorteForm.get('origin_localidad')?.value || '',
        localidadDescripcion: this.cartaPorteForm.get('origin_localidad')?.value || '',
        c_Colonia: this.cartaPorteForm.get('origin_colonia')?.value || '',
        coloniaDescripcion: this.cartaPorteForm.get('origin_colonia')?.value || ''
      };

      // Solo actualizar si hay datos, si no dejar como null
      const hasData = Object.values(addressData).some(value => value && typeof value === 'string' && value.trim() !== '');
      this.cartaPorteForm.get('origin_address')?.setValue(hasData ? addressData : null, { emitEvent: false });
    }
  }

  private fetchColoniasByPostalCodeOrigen(postalCode: string, selectedColonia?: string) {
    if (!postalCode || postalCode.length < 4) {
      this.coloniasOrigen = [];
      return;
    }

    this.loadingColoniasOrigen = true;
    // Convertir a string para asegurar que la API lo maneje correctamente
    this.apiCartaPorte.getColoniasByPostalCode(String(postalCode)).subscribe({
      next: (response: any) => {
        this.loadingColoniasOrigen = false;
        
        if (response && !response.message) { // Success response
          this.coloniasOrigen = response.colonias || [];
          
          // Only set dataOrigin if we have valid Sepomex data (not just postal code)
          if (response.c_CodigoPostal && response.c_Estado) {
            this.dataOrigin = response;
            this.existCodeOrigen = 1;
            
            // Auto-populate address fields with Sepomex data
            this.cartaPorteForm.patchValue({
              origin_pais: 'MEX', // Default country for Mexican postal codes
              origin_estado: response.estadoDescripcion || '',
              origin_municipio: response.municipioDescripcion || '',
              origin_localidad: response.localidadDescripcion || '',
            }, { emitEvent: false });
            
            // Manually trigger updateOriginAddress after setting dataOrigin
            setTimeout(() => {
              this.updateOriginAddress();
            }, 0);
            
          } else {
            this.dataOrigin = null;
            this.existCodeOrigen = 1;
          }

          // Auto-select the specified colonia if provided
          if (selectedColonia) {
            this.cartaPorteForm.patchValue({
              origin_colonia: selectedColonia
            }, { emitEvent: false });
          } else if (this.coloniasOrigen.length === 1) {
            // Auto-select the only available colonia if there's only one
            this.cartaPorteForm.patchValue({
              origin_colonia: this.coloniasOrigen[0].c_Colonia
            }, { emitEvent: false });
          }
        } else {
          this.existCodeOrigen = 2
          this.coloniasOrigen = [];
          this.dataOrigin = {}
          // Clear address fields if no data found
          this.cartaPorteForm.patchValue({
            origin_pais: 'MEX',
            origin_estado: '',
            origin_municipio: '',
            origin_localidad: '',
          }, { emitEvent: false });
        }
      },
      error: (error) => {
        this.existCodeOrigen = 2
        this.loadingColoniasOrigen = false;
        this.dataOrigin = {}
        this.cartaPorteForm.patchValue({
                origin_pais: 'MEX',
                origin_estado: '',
                origin_municipio: '',
                origin_localidad: '',
            }, { emitEvent: false });
        this.coloniasOrigen = [];
      }
    });
  }

  getSelectedColoniaOrigenCode(): string {
    const selectedColoniaValue = this.cartaPorteForm.get('origin_colonia')?.value;
    if (!selectedColoniaValue) {
      return 'MEX'; // Default value
    }
    
    // Find the colonia object by c_Colonia
    const selectedColonia = this.coloniasOrigen.find(c => c.c_Colonia === selectedColoniaValue);
    return selectedColonia ? selectedColonia.c_Colonia : selectedColoniaValue;
  }

  private setupPostalCodeListener() {
    // Esperar a que el DOM esté completamente renderizado
    setTimeout(() => {
      // Escuchar evento blur (cuando el campo pierde el foco)
      const postalCodeElement = document.querySelector('input[formcontrolname="destination_codigo_postal"]');
      if (postalCodeElement) {
        postalCodeElement.addEventListener('blur', (event) => {
          this.onPostalCodeChange(event);
        });
      }

      // Escuchar evento blur para código postal origen
      const postalCodeOrigenElement = document.querySelector('input[formcontrolname="origin_codigo_postal"]');
      if (postalCodeOrigenElement) {
        postalCodeOrigenElement.addEventListener('blur', (event) => {
          this.onPostalCodeOrigenChange(event);
        });
      }
    }, 100); // Pequeño retraso para asegurar que el DOM esté listo
  }

  private updateDestinationAddress() {
    // If we have Sepomex data, use that structure
    if (this.dataDestination && this.dataDestination.c_CodigoPostal) {
      const selectedColonia = this.colonias.find(c => c.c_Colonia === this.cartaPorteForm.get('destination_colonia')?.value);
      
      const sepomexData = {
        c_CodigoPostal: this.dataDestination.c_CodigoPostal,
        c_Estado: this.dataDestination.c_Estado,
        estadoDescripcion: this.dataDestination.estadoDescripcion,
        c_Municipio: this.dataDestination.c_Municipio,
        municipioDescripcion: this.dataDestination.municipioDescripcion,
        c_Localidad: this.dataDestination.c_Localidad,
        localidadDescripcion: this.dataDestination.localidadDescripcion,
        c_Colonia: selectedColonia?.c_Colonia || '',
        coloniaDescripcion: selectedColonia?.descripcion || ''
      };
      
      this.cartaPorteForm.get('destination_address')?.setValue(sepomexData, { emitEvent: false });
    } else {
      // Fallback to basic structure if no Sepomex data
      const addressData = {
        calle: this.cartaPorteForm.get('destination_calle')?.value || '',
        codigo_postal: this.cartaPorteForm.get('destination_codigo_postal')?.value || '',
        pais: this.cartaPorteForm.get('destination_pais')?.value || '',
        estado: this.cartaPorteForm.get('destination_estado')?.value || '',
        municipio: this.cartaPorteForm.get('destination_municipio')?.value || '',
        localidad: this.cartaPorteForm.get('destination_localidad')?.value || '',
        colonia: this.cartaPorteForm.get('destination_colonia')?.value || ''
      };

      // Solo actualizar si hay datos, si no dejar como null
      const hasData = Object.values(addressData).some(value => value && typeof value === 'string' && value.trim() !== '');
      this.cartaPorteForm.get('destination_address')?.setValue(hasData ? addressData : null, { emitEvent: false });
    }
  }

  private fetchColoniasByPostalCode(postalCode: string, selectedColoniaValue?: string) {
    if (!postalCode || postalCode.length < 4) {
      this.colonias = [];
      return;
    }

    this.loadingColonias = true;
    // Convertir a string para asegurar que la API lo maneje correctamente
    this.apiCartaPorte.getColoniasByPostalCode(String(postalCode)).subscribe({
      next: (response: any) => {
        this.loadingColonias = false;
        
        if (response && !response.message) { // Success response
          this.colonias = response.colonias || [];
          this.dataDestination = response;
          this.existCode = 1
          
          // Auto-populate address fields with Sepomex data
          this.cartaPorteForm.patchValue({
            destination_codigo_postal: postalCode,
            destination_pais: 'MEX', // Default country for Mexican postal codes
            destination_estado: response.estadoDescripcion || '',
            destination_municipio: response.municipioDescripcion || '',
            destination_localidad: response.localidadDescripcion || '',
          }, { emitEvent: false });

          // Set the colonia value if provided or if there's only one colonia available
          let coloniaToSet = '';
          
          if (selectedColoniaValue) {
            // Try to find the colonia by code first, then by description
            coloniaToSet = selectedColoniaValue;
            const foundColonia = this.colonias.find(c => 
              c.c_Colonia === selectedColoniaValue || 
              c.descripcion === selectedColoniaValue
            );
            
            if (foundColonia) {
              coloniaToSet = foundColonia.c_Colonia;
            }
          } else if (this.colonias.length === 1) {
            // Auto-select the only available colonia
            coloniaToSet = this.colonias[0].c_Colonia;
          }
          
          if (coloniaToSet) {
            this.cartaPorteForm.patchValue({
              destination_colonia: coloniaToSet
            }, { emitEvent: false });
          }

          // Update destination_address JSON after setting all fields
          this.updateDestinationAddress();
        } else {
          this.existCode = 2
          this.colonias = [];
          this.dataDestination = {}
          // Clear address fields if no data found
          this.cartaPorteForm.patchValue({
            destination_pais: 'MEX',
            destination_estado: '',
            destination_municipio: '',
            destination_localidad: '',
            destination_colonia: selectedColoniaValue || ''
          }, { emitEvent: false });
          
          // Update destination_address JSON even when no data found
          this.updateDestinationAddress();
        }
      },
      error: (error) => {
        this.existCode = 2
        this.loadingColonias = false;
        this.dataDestination = {}
        this.cartaPorteForm.patchValue({
            destination_pais: 'MEX',
            destination_estado: '',
            destination_municipio: '',
            destination_localidad: '',
            destination_colonia: selectedColoniaValue || ''
          }, { emitEvent: false });
        this.colonias = [];
        
        // Update destination_address JSON even when error occurs
        this.updateDestinationAddress();
      }
    });
  }

  onPostalCodeChange(event: any) {
    const postalCode = event.target.value;
    // Convertir a string para la búsqueda, pero manejar tanto strings como números
    const postalCodeStr = String(postalCode || '').trim();
    
    // Solo proceder si el código postal no está vacío
    if (postalCodeStr) {
      this.fetchColoniasByPostalCode(postalCodeStr);
      
      // Clear colonia field when postal code changes
      this.cartaPorteForm.get('destination_colonia')?.setValue('', { emitEvent: false });
    }
  }

  onPostalCodeOrigenChange(event: any) {
    const postalCode = event.target.value;
    // Convertir a string para la búsqueda, pero manejar tanto strings como números
    const postalCodeStr = String(postalCode || '').trim();
    
    // Solo proceder si el código postal no está vacío
    if (postalCodeStr) {
      this.fetchColoniasByPostalCodeOrigen(postalCodeStr);
      
      // Clear colonia field when postal code changes
      this.cartaPorteForm.get('origin_colonia')?.setValue('', { emitEvent: false });
    }
  }

  goBack() {
    this.location.back();
  }
  private setupAddressListeners(fields: string[], onChange: () => void) {
    fields.forEach(field => {
      this.cartaPorteForm.get(field)?.valueChanges.subscribe(() => onChange());
    });

    onChange();
  }
}
